// Seats and entitlements (ADR 0033, docs/specs/plans-and-phone-system.md section 4). Mirrors
// `vd_api_types::plans` and `vd_core::plans`. Clients build navigation from `entitlements`,
// never from the plan name, so a new plan or a grant changes menus without a release.

import type { Me, Roles, Timestamp, Uuid } from "./types.ts";

/**
 * The phone plans, ordered lowest first (Basic, Core, Sales and Service, Sales Pro, Sales Max),
 * and Marketing, outside the nest with its own set (ADR 0049 A2).
 */
export type Plan = "basic" | "core" | "service" | "sales" | "sales_pro" | "marketing";

export type SubscriptionStatus =
  | "trialing"
  | "active"
  | "past_due"
  | "suspended"
  | "canceled"
  | "internal";

export type Entitlement =
  // Basic
  | "calling"
  | "shared_numbers"
  | "voicemail"
  | "contacts"
  | "queue_agent"
  // Core
  | "messaging"
  | "recording"
  | "menu_basic"
  | "call_privacy"
  // Service
  | "ivr"
  | "queues"
  | "agent_status"
  | "callbacks"
  | "ai_summaries"
  | "live_transcript"
  | "call_fields"
  | "dispositions"
  | "dashboards"
  | "analytics"
  | "monitoring"
  | "support_analytics"
  // Sales
  | "today"
  | "lists"
  // Reading lists and contacts: Core and up, and the Marketing seat (ADR 0049 A2).
  | "lists_read"
  | "focus"
  | "power_dial"
  | "parallel_dial"
  | "voicemail_drops"
  | "campaigns"
  | "research"
  | "assistant"
  | "email_follow_ups"
  | "mailbox"
  | "meeting_invites"
  | "connector"
  | "performance_reviews"
  | "mark_won"
  // In no phone plan: a platform grant or the Marketing seat gives it (ADR 0049,
  // `vd_core::plans::UNPLANNED`).
  | "email_marketing";

/** `Me.seat`. */
export interface MeSeat {
  /** The phone seat. `null`: none (a console-only admin or manager, or a Marketing-only member). */
  plan: Plan | null;
  /** A Marketing seat beside the phone seat, or alone (the owner, 2026-11-01). Absent: none. */
  marketing?: boolean;
  status: SubscriptionStatus;
  entitlements: Entitlement[];
  /** What the company holds across its seats. */
  tenant_entitlements: Entitlement[];
  plan_version: string;
}

/** The error code of a route the seat does not include; the entitlement is in the
 * `x-vd-entitlement` response header. */
export const NOT_IN_PLAN = "not_in_plan";
export const ENTITLEMENT_HEADER = "x-vd-entitlement";

/** Plan names as people read them (the owner's names of 2026-10-26; the keys stay). */
export const PLAN_LABEL: Record<Plan, string> = {
  basic: "Basic",
  core: "Core",
  service: "Sales and Service",
  sales: "Sales Pro",
  sales_pro: "Sales Max",
  marketing: "Marketing",
};

/** The nested phone plans, lowest first. */
export const PLANS: Plan[] = ["basic", "core", "service", "sales", "sales_pro"];

/** The seat outside the nest: no phone (ADR 0049 A2). */
export const MARKETING_PLAN: Plan = "marketing";

/** Every plan: the nest, then Marketing (`vd_core::plans::Plan::ALL`). */
export const ALL_PLANS: Plan[] = [...PLANS, MARKETING_PLAN];

/** `vd_core::plans::MARKETING_SET`: the Marketing seat's whole set. */
export const MARKETING_SET: Entitlement[] = [
  "email_marketing",
  "assistant",
  "campaigns",
  "mailbox",
  "connector",
  "lists_read",
];

/**
 * Whether the member has an entitlement. A server older than plans sends no `seat`: everything
 * stays as it was.
 */
export function hasEnt(me: Pick<Me, "seat"> | null | undefined, e: Entitlement): boolean {
  const seat = me?.seat;
  return seat ? seat.entitlements.includes(e) : true;
}

/** Whether the company holds an entitlement on any seat (admin pages for the feature). */
export function tenantHasEnt(me: Pick<Me, "seat"> | null | undefined, e: Entitlement): boolean {
  const seat = me?.seat;
  return seat ? seat.tenant_entitlements.includes(e) : true;
}

/** The member's seat plan, or `null` (no seat, or a server older than plans). */
export function seatPlan(me: Pick<Me, "seat"> | null | undefined): Plan | null {
  return me?.seat?.plan ?? null;
}

// Console: Plan and seats ----------------------------------------------------------------------

export type Meter = "voice_minutes" | "sms_segments" | "ai_usd" | "research_usd" | "pdial_lines";
export type CapState = "under" | "warning" | "reached";

export interface SeatCount {
  plan: Plan;
  /** `null`: no cap (internal). */
  purchased: number | null;
  assigned: number;
}

export interface MeterView {
  meter: Meter;
  label: string;
  unit: "usd" | "minute" | "segment" | "line";
  used: number;
  included: number;
  purchased: number;
  hard_cap: boolean;
  state: CapState;
}

export interface ScheduledSeatChange {
  id: Uuid;
  user_id: Uuid;
  name: string;
  from: Plan | null;
  to: Plan | null;
  /** The Marketing seat before and after. */
  from_marketing?: boolean;
  to_marketing?: boolean;
  effective_at: Timestamp;
}

export interface MemberSeat {
  user_id: Uuid;
  name: string;
  email: string;
  roles: Roles;
  /** The phone seat. */
  seat: Plan | null;
  /** A Marketing seat beside it, or alone. */
  marketing?: boolean;
  scheduled?: ScheduledSeatChange;
}

export interface BillingProviderView {
  provider: string | null;
  checkout_url: string | null;
  portal_url: string | null;
}

/** `GET /v1/admin/plan`. */
export interface PlanOverview {
  status: SubscriptionStatus;
  plan_version: string;
  period_start: Timestamp | null;
  period_end: Timestamp | null;
  /** Whole days left while trialing (day one reads 14). Never computed by clients. */
  trial_days_left?: number;
  seats: SeatCount[];
  meters: MeterView[];
  scheduled: ScheduledSeatChange[];
  members: MemberSeat[];
  billing: BillingProviderView;
}

export type SeatWhen = "period_end" | "now";

/** `PUT /v1/admin/users/{id}/seat` and the preview's body. */
export interface SeatChange {
  /** The phone seat; `null`: none. */
  plan: Plan | null;
  /** The Marketing seat beside it; absent keeps what the member has. */
  marketing?: boolean;
  when?: SeatWhen;
}

export type DowngradeEffect =
  | "phone_stops"
  | "texts_stop"
  | "recording_stops"
  | "summaries_stop"
  | "dashboards_read_only"
  | "monitoring_stops"
  | "leaves_queues"
  | "focus_stops"
  | "power_dial_stops"
  | "parallel_dial_stops"
  | "lists_read_only"
  | "research_stops"
  | "assistant_stops"
  | "email_drafts_stop"
  | "reviews_pause"
  | "voicemail_drops_stop"
  | "marketing_emails_stop";

/** `POST /v1/admin/users/{id}/seat/preview`. */
export interface SeatPreview {
  from: Plan | null;
  to: Plan | null;
  from_marketing?: boolean;
  to_marketing?: boolean;
  downgrade: boolean;
  effective_at: Timestamp;
  period_end: Timestamp | null;
  effects: { effect: DowngradeEffect; label: string }[];
  /** Why it cannot be saved ("Pick a seat.", "No Sales seats left."). */
  refused?: string;
}

/** `PUT /v1/admin/users/{id}/seat`. */
export interface SeatChangeResult {
  user_id: Uuid;
  seat: Plan | null;
  marketing?: boolean;
  scheduled?: ScheduledSeatChange;
}

export interface StatementLine {
  key: string;
  label: string;
  quantity: number;
  unit: string;
  unit_usd: number;
  usd: number;
}

export interface StatementView {
  id: Uuid;
  period_start: Timestamp;
  period_end: Timestamp;
  plan_version: string;
  lines: StatementLine[];
  total_usd: number;
  state: "draft" | "final" | "sent";
}

// The route registry (crates/server/src/plans/registry.rs) --------------------------------------

/** What a route needs from the member's seat (`plans::registry::Rule`). */
export type RuleKind = "open" | "seat" | "seat_any" | "seat_or_team" | "tenant";
export type Rule =
  | [path: string, kind: "open"]
  | [path: string, kind: "seat_any", ents: Entitlement[]]
  | [path: string, kind: Exclude<RuleKind, "open" | "seat_any">, ent: Entitlement];

/** A rule for one HTTP method. It beats a `ROUTE_RULES` entry of the same length. */
export type MethodRule = [method: string, ...rule: Rule];

/** Contact reads: a phone seat's `contacts` or the Marketing seat's `lists_read`. */
const CONTACT_READ: Entitlement[] = ["contacts", "lists_read"];

/**
 * Every `/v1` route prefix and what it needs for every method, the same list as the server's
 * `"*"` rules (a test reads the Rust file and fails when the two differ). A prefix ending in `$`
 * covers only that path.
 */
export const ROUTE_RULES: Rule[] = [
  ["/v1/auth", "open"],
  ["/v1/auth/microsoft/mobile", "open"],
  ["/v1/me$", "open"],
  ["/v1/me/settings", "open"],
  ["/v1/me/passkeys", "open"],
  ["/v1/me/password", "open"],
  ["/v1/me/sign-in", "open"],
  ["/v1/me/emergency-location", "open"],
  ["/v1/me/crm-access", "open"],
  ["/v1/me/platform-handoff", "open"],
  ["/v1/devices", "open"],
  ["/v1/phone/claim", "open"],
  ["/v1/platform", "open"],
  ["/v1/public", "open"],
  ["/v1/signup", "open"],
  ["/v1/agent-setup", "open"],
  ["/v1/admin/agent-setup", "open"],
  ["/v1/me/availability", "seat", "calling"],
  ["/v1/me/blocked", "seat", "calling"],
  ["/v1/me/greetings", "seat", "voicemail"],
  ["/v1/me/vm-default", "seat", "voicemail_drops"],
  ["/v1/me/connector", "seat", "connector"],
  ["/v1/me/dashboard-default", "seat", "dashboards"],
  ["/v1/me/dashboard-prefs", "seat", "dashboards"],
  ["/v1/me/sending-addresses", "seat", "email_marketing"],
  ["/v1/openapi.json", "open"],
  ["/v1/ws", "open"],
  ["/v1/telemetry", "open"],
  ["/v1/desktop", "open"],
  ["/v1/whats-new", "open"],
  ["/v1/photos", "open"],
  ["/v1/notifications", "open"],
  ["/v1/integrations", "open"],
  ["/v1/mobile-spike", "open"],
  ["/v1/bug-reports", "open"],
  ["/v1/assistant/unread", "open"],
  ["/v1/assistant/sessions", "open"],
  ["/v1/assistant/budget", "open"],
  ["/v1/assistant/conversations", "seat", "assistant"],
  ["/v1/assistant/instructions", "seat", "assistant"],
  ["/v1/ai", "open"],
  ["/v1/telephony", "seat", "calling"],
  ["/v1/lookup", "seat", "calling"],
  ["/v1/calls", "seat", "calling"],
  ["/v1/calls/{}/recording", "open"],
  ["/v1/calls/{}/fields", "seat", "call_fields"],
  ["/v1/calls/{}/monitor", "tenant", "monitoring"],
  ["/v1/calls/{}/email-draft", "seat", "email_follow_ups"],
  ["/v1/calls/{}/meeting", "seat", "meeting_invites"],
  ["/v1/calls/{}/tracker-suggestions", "seat", "dashboards"],
  ["/v1/calls/{}/voicemail-drop", "seat", "voicemail_drops"],
  ["/v1/calls/{}/class", "seat", "call_privacy"],
  ["/v1/number-marks", "seat", "call_privacy"],
  ["/v1/wrapup-drafts", "seat", "calling"],
  ["/v1/voicemails", "seat", "voicemail"],
  ["/v1/messages", "seat", "messaging"],
  ["/v1/contacts", "seat", "contacts"],
  ["/v1/contacts/{}/trackers", "seat", "dashboards"],
  ["/v1/contacts/{}/email-dnc", "seat", "email_follow_ups"],
  ["/v1/crm", "seat", "contacts"],
  ["/v1/dispositions", "seat", "dispositions"],
  ["/v1/team$", "open"],
  ["/v1/team/live", "tenant", "monitoring"],
  ["/v1/team/{}/calls", "open"],
  ["/v1/team/{}/coaching", "tenant", "monitoring"],
  ["/v1/coaching", "open"],
  ["/v1/analytics", "seat_or_team", "analytics"],
  ["/v1/opportunities", "seat", "mark_won"],
  ["/v1/dashboards", "seat_or_team", "dashboards"],
  ["/v1/call-fields", "seat_or_team", "call_fields"],
  ["/v1/approval-thresholds", "seat_or_team", "call_fields"],
  ["/v1/approvals", "seat_or_team", "call_fields"],
  ["/v1/trackers", "seat_or_team", "dashboards"],
  ["/v1/tracker-items", "seat_or_team", "dashboards"],
  ["/v1/tracker-events", "seat_or_team", "dashboards"],
  ["/v1/tracker-suggestions", "seat_or_team", "dashboards"],
  ["/v1/reviews", "seat_or_team", "performance_reviews"],
  ["/v1/today", "seat", "today"],
  ["/v1/today/next", "seat", "focus"],
  ["/v1/today/upcoming", "seat", "focus"],
  ["/v1/today/queue", "seat", "focus"],
  ["/v1/sync", "seat", "today"],
  ["/v1/sessions", "seat", "focus"],
  ["/v1/lists", "seat_or_team", "lists"],
  ["/v1/lists/{}/build", "seat", "research"],
  ["/v1/lists/{}/campaign", "seat_or_team", "campaigns"],
  ["/v1/lists/{}/session-summary", "seat", "focus"],
  ["/v1/lists/{}/tracker", "seat_or_team", "dashboards"],
  ["/v1/lists/{}/vm-messages", "seat", "voicemail_drops"],
  ["/v1/imports", "seat_or_team", "lists"],
  ["/v1/pdial", "seat", "parallel_dial"],
  ["/v1/vm-messages", "seat", "voicemail_drops"],
  ["/v1/campaigns", "seat_or_team", "campaigns"],
  ["/v1/campaigns/{}/costs", "seat_or_team", "dashboards"],
  ["/v1/campaigns/{}/dashboards", "seat_or_team", "dashboards"],
  ["/v1/campaigns/{}/vm-default", "seat", "voicemail_drops"],
  ["/v1/campaigns/{}/designs", "seat", "email_marketing"],
  ["/v1/designs", "seat", "email_marketing"],
  ["/v1/campaigns/{}/sends", "seat", "email_marketing"],
  ["/v1/sends", "seat", "email_marketing"],
  ["/v1/templates", "seat", "email_marketing"],
  ["/v1/brief-template", "seat_or_team", "campaigns"],
  ["/v1/brands", "seat", "email_marketing"],
  ["/v1/brief-interviews", "seat_or_team", "campaigns"],
  ["/v1/suggestions", "seat", "assistant"],
  ["/v1/artifacts", "seat", "assistant"],
  ["/v1/research", "seat", "research"],
  ["/v1/leads", "seat", "research"],
  ["/v1/email", "seat", "email_follow_ups"],
  ["/v1/email/accounts", "seat", "mailbox"],
  ["/v1/mailboxes", "seat", "mailbox"],
  ["/v1/meetings", "seat", "meeting_invites"],
  ["/v1/drafts", "seat", "mailbox"],
  ["/v1/email/oauth", "open"],
  ["/v1/admin", "open"],
  ["/v1/emergency", "open"],
  ["/v1/admin/pdial", "tenant", "parallel_dial"],
  ["/v1/admin/vm-drops", "tenant", "voicemail_drops"],
  ["/v1/admin/campaigns", "tenant", "campaigns"],
  ["/v1/admin/brief-template", "tenant", "campaigns"],
  ["/v1/admin/research", "tenant", "research"],
  ["/v1/admin/assistant", "tenant", "assistant"],
  ["/v1/admin/email", "tenant", "email_follow_ups"],
  ["/v1/admin/company-mailboxes", "tenant", "mailbox"],
  ["/v1/admin/mailbox-assignments", "tenant", "mailbox"],
  ["/v1/admin/meetings", "tenant", "meeting_invites"],
  ["/v1/admin/email/admin-consent/callback", "open"],
  ["/v1/admin/connector", "tenant", "connector"],
  ["/v1/admin/reviews", "tenant", "performance_reviews"],
  ["/v1/admin/imports", "tenant", "lists"],
  ["/v1/admin/dispositions", "tenant", "dispositions"],
  ["/v1/admin/private-minutes", "open"],
  ["/v1/admin/private-switches", "open"],
  ["/v1/admin/billing", "open"],
  ["/v1/admin/setup", "open"],
  // Data export and account closure (ADR 0062): open, so a closing company reaches them.
  ["/v1/admin/exports", "open"],
  ["/v1/admin/closure", "open"],
  ["/v1/admin/sending-domains", "tenant", "email_marketing"],
  ["/v1/admin/company-addresses", "tenant", "email_marketing"],
  ["/v1/admin/sending-grants", "tenant", "email_marketing"],
  ["/v1/admin/marketing-settings", "tenant", "email_marketing"],
  ["/v1/admin/sending-policy", "tenant", "email_marketing"],
];

/** The server's method-specific rules: reading lists and contacts (ADR 0049 A2). */
export const METHOD_RULES: MethodRule[] = [
  ["GET", "/v1/contacts", "seat_any", CONTACT_READ],
  ["GET", "/v1/crm/contacts", "seat_any", CONTACT_READ],
  ["POST", "/v1/crm/contacts/resolve", "seat_any", CONTACT_READ],
  ["GET", "/v1/crm/tags", "seat_any", CONTACT_READ],
  ["GET", "/v1/lists", "seat_or_team", "lists_read"],
];

function segments(path: string): string[] {
  const bare = path.replace(/\$$/, "").split(/[?#]/, 1)[0] ?? "";
  return bare
    .split("/")
    .filter((s) => s !== "")
    .map((s) => (s.startsWith("{") ? "{}" : s));
}

/**
 * The rule for a request path (`/v1/lists/0192.../items`) or a route template. Concrete ids
 * match `{}` in a rule only where the rule has a parameter, so literal segments in a rule win.
 * With a method, a rule for that method beats a rule for every method of the same length;
 * without one, only the rules for every method count.
 */
export function ruleFor(path: string, method?: string): Rule | null {
  const ps = segments(path);
  let best: Rule | null = null;
  let bestLen = -1;
  let bestSpecific = false;
  const candidates: [Rule, boolean][] = ROUTE_RULES.map((r) => [r, false]);
  if (method) {
    const m = method.toUpperCase();
    for (const [rm, ...rule] of METHOD_RULES) {
      if (rm === m) candidates.push([rule as Rule, true]);
    }
  }
  for (const [rule, specific] of candidates) {
    const exact = rule[0].endsWith("$");
    const pat = segments(rule[0]);
    if (pat.length > ps.length || (exact && pat.length !== ps.length)) continue;
    if (!pat.every((seg, i) => seg === "{}" || seg === ps[i])) continue;
    if (pat.length > bestLen || (pat.length === bestLen && specific && !bestSpecific)) {
      best = rule;
      bestLen = pat.length;
      bestSpecific = specific;
    }
  }
  return best;
}

/**
 * The entitlement a request lacks for this member, or null when it may go. A server older than
 * plans (no `seat`) never refuses. Mirrors the server's gate (`plans::SeatCtx::passes`).
 */
export function missingFor(
  me: Pick<Me, "seat" | "roles"> | null | undefined,
  path: string,
  method?: string,
): Entitlement | null {
  const seat = me?.seat;
  if (!seat || !path.startsWith("/v1/")) return null;
  const rule = ruleFor(path, method);
  if (!rule || rule[1] === "open") return null;
  if (rule[1] === "seat_any") {
    const ents = rule[2];
    return ents.some((x) => seat.entitlements.includes(x)) ? null : (ents[0] ?? null);
  }
  const e = rule[2];
  const own = seat.entitlements.includes(e);
  const tenant = seat.tenant_entitlements.includes(e);
  const manages = !!(me?.roles?.manager || me?.roles?.admin);
  // A Marketing seat never gets list or contact writes through a manager's team reach.
  const reach = !(marketingOnly(seat) && MARKETING_READ_ONLY.includes(e));
  const ok =
    rule[1] === "seat"
      ? own
      : rule[1] === "seat_or_team"
        ? own || (manages && tenant && reach)
        : tenant;
  return ok ? null : e;
}

/** `vd_core::plans::MARKETING_READ_ONLY`. */
const MARKETING_READ_ONLY: Entitlement[] = ["lists", "contacts"];

// The plan book, for clients that compute a seat themselves (mock mode) ---------------------------

/**
 * The lowest phone plan that includes each entitlement; `null`: none does (only a grant or the
 * Marketing seat gives it).
 */
const MIN_PLAN: Record<Entitlement, Plan | null> = {
  calling: "basic",
  shared_numbers: "basic",
  voicemail: "basic",
  contacts: "basic",
  queue_agent: "basic",
  messaging: "core",
  recording: "core",
  menu_basic: "core",
  call_privacy: "core",
  ivr: "service",
  queues: "service",
  agent_status: "service",
  callbacks: "service",
  ai_summaries: "service",
  live_transcript: "service",
  call_fields: "service",
  dispositions: "service",
  dashboards: "service",
  analytics: "service",
  monitoring: "service",
  support_analytics: "service",
  today: "sales",
  lists: "service",
  lists_read: "core",
  focus: "service",
  power_dial: "sales",
  parallel_dial: "sales_pro",
  voicemail_drops: "sales",
  campaigns: "sales",
  research: "sales",
  assistant: "service",
  email_follow_ups: "service",
  mailbox: "service",
  meeting_invites: "service",
  connector: "sales",
  performance_reviews: "sales",
  mark_won: "sales",
  email_marketing: null,
};

export const ENTITLEMENTS = Object.keys(MIN_PLAN) as Entitlement[];

/** `vd_core::plans::SALES_TOOLS`: never a Support agent's without Rep, on any seat. */
export const SALES_TOOLS: Entitlement[] = [
  "today",
  "lists",
  "lists_read",
  "focus",
  "power_dial",
  "parallel_dial",
  "voicemail_drops",
  "campaigns",
  "research",
  "assistant",
  "email_follow_ups",
  "connector",
  "performance_reviews",
  "mark_won",
  "email_marketing",
];

/** A Marketing seat and no phone seat (`vd_core::plans::Holding::marketing_only`). */
export function marketingOnly(seat: Pick<MeSeat, "plan" | "marketing">): boolean {
  return seat.plan === MARKETING_PLAN || (seat.plan === null && !!seat.marketing);
}

/** `vd_core::plans::ADDONS`: sold as an add-on to some phone seats only. */
export const ADDONS: Entitlement[] = ["email_marketing"];

/** `vd_core::plans::ADDON_PLANS`: Sales Pro (`sales`) and Sales Max (`sales_pro`). */
export const ADDON_PLANS: Plan[] = ["sales", "sales_pro"];

/** `vd_core::plans::addon_allowed`: whether a grant of `e` reaches a member on this phone seat. */
export function addonAllowed(plan: Plan | null, e: Entitlement): boolean {
  return !ADDONS.includes(e) || (plan !== null && ADDON_PLANS.includes(plan));
}

/** `vd_core::plans::plan_has`: nesting for the phone plans, Marketing's own set. */
export function planHas(plan: Plan, e: Entitlement): boolean {
  if (plan === MARKETING_PLAN) return MARKETING_SET.includes(e);
  const min = MIN_PLAN[e];
  return min !== null && PLANS.indexOf(min) <= PLANS.indexOf(plan);
}

/**
 * The entitlements of a member's seats for these roles (`vd_core::plans::effective` with an
 * active status and no grants): the phone plan's set and, with `marketing`, the Marketing set
 * (the owner, 2026-11-01: a Marketing seat stacks on the phone seat). A Support agent without
 * Rep never gets the Sales tools, except email marketing through a Marketing seat.
 */
export function entitlementsOf(
  plan: Plan | null,
  roles: Pick<Roles, "rep" | "support">,
  marketing = false,
): Entitlement[] {
  const withMarketing = marketing || plan === MARKETING_PLAN;
  const phone = plan === MARKETING_PLAN ? null : plan;
  return ENTITLEMENTS.filter((e) => {
    const held =
      (phone !== null && planHas(phone, e)) || (withMarketing && planHas(MARKETING_PLAN, e));
    if (!held) return false;
    if (withMarketing && e === "email_marketing") return true;
    return !(roles.support && !roles.rep && SALES_TOOLS.includes(e));
  });
}

/** A request refused before it was sent: the seat does not include it. */
export const NOT_IN_PLAN_STATUS = 403;
